import { Data, Effect, Filter, Schedule } from 'effect';
import { HttpClient, HttpClientError, type HttpClientRequest, type HttpMethod } from 'effect/http';
import { BAD_GATEWAY_STATUS, GATEWAY_TIMEOUT_STATUS, INTERNAL_SERVER_ERROR_STATUS, RATE_LIMITED_STATUS, REQUEST_TIMEOUT_STATUS, SERVICE_UNAVAILABLE_STATUS } from './http-status';

/** Delay before the first retry of a transient failure, in milliseconds. */
const RETRY_BASE_DELAY = 100;

/** Number of retries a transient failure gets before the call fails. */
const RETRY_TIMES = 3;

/**
 * Statuses that say an origin could not answer this time, rather than that it never will.
 *
 * Named rather than listed as numbers, because what each of them means is the whole reason it is in here: every one is a "not now" and none of them is a "no". A 404 or a 422 would answer the same way however many times it were asked. See `http-status` for what each one says.
 */
const TRANSIENT_STATUSES: ReadonlySet<number> = new Set([REQUEST_TIMEOUT_STATUS, RATE_LIMITED_STATUS, INTERNAL_SERVER_ERROR_STATUS, BAD_GATEWAY_STATUS, SERVICE_UNAVAILABLE_STATUS, GATEWAY_TIMEOUT_STATUS]);

/**
 * The methods where sending the same request twice means the same as sending it once.
 *
 * POST and PATCH are the two left out, and deliberately: a POST sent a second time creates a second thing. Everything listed here either reads, or writes a state the caller already spelled out in full, so a repeated attempt lands on the same resource rather than making another one.
 */
const IDEMPOTENT_METHODS: ReadonlySet<HttpMethod.HttpMethod> = new Set(['DELETE', 'GET', 'HEAD', 'OPTIONS', 'PUT', 'TRACE']);

/**
 * Raised when the request never reached its origin, and retrying did not help.
 *
 * The browser could not complete the exchange at all — offline, DNS failure, connection refused, blocked by CORS. This is only raised once the whole retry schedule is spent, so it means a sustained failure rather than a blip: every recoverable attempt has already been made.
 */
export class NetworkError extends Data.TaggedError('NetworkError')<{
	readonly method: HttpClientRequest.HttpClientRequest['method'];
	readonly url: string;
	readonly cause: unknown;
}> {}

/**
 * Passes only the failures where nothing reached the origin, unwrapped to the transport reason itself.
 *
 * `HttpClientError` keeps its detail in `reason`, so the transport case cannot be selected by tag alone — which is exactly what `Filter.reason` is for. Anything it does not pass stays in the error channel untouched.
 */
const transportFailure = Filter.reason('HttpClientError', 'TransportError');

/**
 * Whether a failed attempt may be made again, weighed against what a second attempt would cost.
 *
 * Transience alone is not enough to decide. A lost response is indistinguishable from a request that never arrived, so retrying a POST on one risks a second calculation job, a second order, a second of whatever that call creates — the API takes no idempotency key, so nothing on the other end would recognise the
 * replay. A non-idempotent call is therefore only sent again on the one failure that proves the origin never acted on it: 429, a refusal to handle the request rather than a failure while handling it. Idempotent calls keep the whole transient set, dead transport included, since repeating one of those leaves nothing
 * behind.
 */
const isRetryable = (error: unknown): boolean => {
	if (!HttpClientError.isHttpClientError(error)) {
		return false;
	}
	const { reason } = error;
	if (reason._tag === 'TransportError') {
		return IDEMPOTENT_METHODS.has(reason.request.method);
	}
	if (reason._tag === 'StatusCodeError') {
		const { status } = reason.response;
		return status === RATE_LIMITED_STATUS || (TRANSIENT_STATUSES.has(status) && IDEMPOTENT_METHODS.has(reason.request.method));
	}
	return false;
};

/**
 * How every origin the Configurator talks to is spoken to, whatever is being asked of it.
 *
 * The half of a client that is about the exchange rather than about the service: a non-2xx is a failure, a transient one is worth another go, a dead transport is said plainly, and no trace headers go out. What differs per origin — which host, which headers, what a 401 means there — is the caller's to apply before
 * handing the client here.
 *
 * Shared because both origins need all four and neither wants its own version. The retry rule in particular is the kind that must not be written twice: it is the one place that keeps a POST from being sent again, and a second copy would be a second thing to keep right.
 *
 * @param client The client with whatever its own origin requires already applied.
 */
export const withTransportConventions = <E, R>(client: HttpClient.HttpClient.With<E, R>): HttpClient.HttpClient.With<E | HttpClientError.HttpClientError | NetworkError, R> =>
	client.pipe(
		HttpClient.filterStatusOk,
		// Retried by hand rather than through retryTransient, because that combinator only ever
		// widens what counts as transient and what is wanted here is the narrowing in isRetryable,
		// which keeps a POST from being sent twice. Errors only: filterStatusOk above has already
		// turned every transient status into a StatusCodeError by this point.
		HttpClient.transformResponse(
			Effect.retry({
				schedule: Schedule.exponential(RETRY_BASE_DELAY),
				times: RETRY_TIMES,
				while: isRetryable,
			}),
		),
		// After the retry, and that ordering is the whole point. Both wrap the response effect, so
		// pipe order is wrapping order: this only sees an error once the retry schedule is spent.
		// Placed before it, a TransportError would become a NetworkError, which isRetryable does
		// not recognise — and nothing would ever be retried.
		HttpClient.transformResponse(
			Effect.catchFilter(transportFailure, (reason) =>
				Effect.fail(
					new NetworkError({
						cause: reason.cause,
						method: reason.request.method,
						url: reason.request.url,
					}),
				),
			),
		),
		// Neither origin accepts a request carrying W3C trace context. The API rejects it outright;
		// the drawing CDN answers no Access-Control-Allow-Headers, so a `traceparent` would make the
		// request preflighted and the browser would block it. The client span is still opened, and
		// never written into the headers: no `traceparent`, no `b3`. Set as a reference rather than
		// by removing the headers, because they are added inside the client that runs the request,
		// after everything here has had its say. Outermost, so a retried attempt is covered too.
		HttpClient.transformResponse(Effect.provideService(HttpClient.TracerPropagationEnabled, false)),
	);
